import { Button } from '@tunasong/ui-lib'
import { useState } from 'react'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import { EarTrainingDirectionPicker } from './ear-training-direction-picker.js'
import type { DirectionParam, SubMode } from './ear-training-params.js'
import styles from './ear-training-welcome.module.css'

export interface EarTrainingWelcomeProps {
  /** Pre-selected sub-mode from `?mode=` URL param, or `null` for no pre-selection. */
  preSelected: SubMode | null
  /** Pre-selected direction from `?direction=`. Only honored when sub-mode is `interval`. */
  preSelectedDirection?: DirectionParam | null
  /** Direction is passed only for the interval sub-mode; undefined otherwise. */
  onStart: (subMode: SubMode, direction?: DirectionParam) => void
}

/**
 * Ear Training Suite welcome screen. v1.5 grows the picker to 3 cards
 * (Interval ID, Chord-quality ID, Aug/Dim Warm-up) and reveals a Direction
 * picker beneath the cards when Interval ID is the active selection.
 *
 * Spec: docs/superpowers/specs/2026-05-21-ear-training-v15-design.md §5.1
 */
export const EarTrainingWelcome: FC<EarTrainingWelcomeProps> = props => {
  const { preSelected, preSelectedDirection, onStart } = props
  const [selected, setSelected] = useState<SubMode | null>(preSelected)
  const [direction, setDirection] = useState<DirectionParam>(preSelectedDirection ?? 'ascending')
  const intro = useTheoryCopy('ear-training-welcome-intro')
  const warmupCopy = useTheoryCopy('ear-training-aug-dim-warmup')
  return (
    <section className={styles.welcome} aria-label="Ear Training welcome">
      {intro?.body ? <p className={styles.intro}>{intro.body}</p> : null}
      <p className={styles.intro}>Pick a sub-mode below, then start a 10-question session.</p>
      <div className={styles.cardGrid}>
        <button
          type="button"
          className={styles.card}
          aria-pressed={selected === 'interval'}
          onClick={() => setSelected('interval')}
        >
          <h3 className={styles.cardTitle}>Interval ID</h3>
          <p className={styles.cardBody}>Hear two notes, identify the interval.</p>
          <p className={styles.cardMeta}>12 intervals · 10 questions</p>
        </button>
        <button
          type="button"
          className={styles.card}
          aria-pressed={selected === 'quality'}
          onClick={() => setSelected('quality')}
        >
          <h3 className={styles.cardTitle}>Chord-quality ID</h3>
          <p className={styles.cardBody}>Hear a chord, identify the quality.</p>
          <p className={styles.cardMeta}>7 qualities · 10 questions</p>
        </button>
        <button
          type="button"
          className={styles.card}
          aria-pressed={selected === 'aug-dim-warmup'}
          onClick={() => setSelected('aug-dim-warmup')}
        >
          <h3 className={styles.cardTitle}>Aug/Dim Warm-up</h3>
          <p className={styles.cardBody}>
            {warmupCopy?.body ?? 'Compare augmented and diminished against major / minor.'}
          </p>
          <p className={styles.cardMeta}>2-stimulus · 10 questions</p>
        </button>
      </div>
      {selected === 'interval' ? (
        <EarTrainingDirectionPicker value={direction} onChange={setDirection} />
      ) : null}
      <Button
        variant="contained"
        color="primary"
        size="large"
        disabled={selected === null}
        onClick={() => {
          if (selected === 'interval') {
            onStart(selected, direction)
            return
          }
          if (selected !== null) {
            onStart(selected, undefined)
          }
        }}
      >
        Start session
      </Button>
    </section>
  )
}
